import { JSONSchema7 } from 'json-schema'
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Input, InputLabel } from '@mui/material'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import KindColor from '../../../components/kindColors'
import ErrorDisplay from '../../../components/error/ErrorDisplay'
import Form from '../../form/ComponentForm'
import ErrorModal from '../../modals/Error'
import { GraphNode, GraphResToTree, GraphResponse, NodeAction, NodeData, Tree } from './NodeData'

import styles from './NodeEditorTab.module.css'
import { getHeaders } from '../../../utils/common'
import ControlPanel from '../ControlPanel'
import Loading from '../../../Loading'
import { Elements } from 'react-flow-renderer'

function getSelectedNode(nodes: any): GraphNode | null {
  if (nodes !== null && nodes.length === 1 && nodes[0].data && (nodes[0].data.path || '').length > 0) {
    return nodes[0] as GraphNode
  }
  return null
}

type nodeInfo = {
  path: string
  config: any
  schema: JSONSchema7
  uiSchema: any
}

let cachedInfo: {
  [key: string]: nodeInfo | null
} = {}

let rememberedPath = ''

type kindOption = {
  name: string
  is_deprecated: boolean
  summary: string
  description: string
  categories: string[]
}

const cachedOptions: {
  [key: string]: string[]
} = {}

function createCopyPatch(sourceData: NodeAction, destData: NodeData): NodeAction {
  // First, see if the destination node has an action for adding the same kind
  // as our copy source.
  const destActions = destData.actions || []
  for (let i = 0; i < destActions.length; i++) {
    const action = destActions[i]
    if (action.operation === 'add' && action.kind === sourceData.kind) {
      return {
        operation: 'copy',
        path: action.path + '/-',
        value: sourceData.path,
      }
    }
  }

  if (sourceData.kind !== destData.kind) {
    throw new Error(`cannot paste a ${sourceData.kind} component onto a ${destData.kind}`)
  }
  return {
    operation: 'copy',
    path: destData.path,
    value: sourceData.path,
  }
}

type appParams = {
  sessionID: string
  fileName: string
}

interface NodeEditorParams {
  readOnly: boolean
  canUndo: boolean
  canRedo: boolean
  submitUndo: () => any
  submitRedo: () => any
  setTree: (tree: Tree) => void
  allNodes: Elements<NodeData>
  allEdges: Elements<NodeData>
  selectedNodes: Elements<NodeData> | null
  setSelectedNodes: any
}

/**
 * NodeEditor component for managing the configuration of a specific node.
 *
 * @param {object} NodeEditorParams - Props for the NodeEditor component.
 * @param {boolean} NodeEditorParams.readOnly - Indicates whether the editor is in read-only mode.
 * @param {boolean} NodeEditorParams.canUndo - Indicates whether undo action is available.
 * @param {boolean} NodeEditorParams.canRedo - Indicates whether redo action is available.
 * @param {Function} NodeEditorParams.submitUndo - Function to perform the undo action.
 * @param {Function} NodeEditorParams.submitRedo - Function to perform the redo action.
 * @param {Function} NodeEditorParams.setTree - Function to set the configuration tree.
 * @param {array} NodeEditorParams.allNodes - Array of all nodes in the configuration.
 * @param {array} NodeEditorParams.allEdges - Array of all edges in the configuration.
 * @param {array} NodeEditorParams.selectedNodes - Array of selected nodes in the editor.
 * @param {Function} NodeEditorParams.setSelectedNodes - Function to set the selected nodes.
 * @returns {JSX.Element} The rendered NodeEditor component.
 */
export default function NodeEditor({
  readOnly,
  canUndo,
  canRedo,
  submitUndo,
  submitRedo,
  setTree,
  allNodes,
  allEdges,
  selectedNodes,
  setSelectedNodes,
}: NodeEditorParams) {
  const { sessionID, fileName } = useParams<appParams>()
  const fetchNodeURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/tree/node`
  const configURL = `/app/view/s/${sessionID}/${fileName}/config`

  const [editingTypeOf, setEditingTypeOf] = useState('')
  const [kindOptions, setKindOptions] = useState<string[] | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<Error | null>(null)
  const [patchError, setPatchError] = useState<Error | null>(null)
  const [info, setInfo] = useState<any>(null)
  const [checkSwitch, setCheckSwitch] = useState<any>()
  const [deltedNumberIndex, setDeltedNumberIndex] = useState<number[]>()
  const [copyError, setCopyError] = useState<string>()

  // Used by copy/paste
  const [copiedNode, setCopiedNode] = useState<NodeAction | null>(null)

  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')

  // Clear cached schemas when the nodes change.
  useEffect(() => {
    cachedInfo = {}
  }, [allNodes])

  // Ensure editingTypeOf isn't pointing to a lost or new selection.
  ;(() => {
    const node = getSelectedNode(selectedNodes)
    if (editingTypeOf !== '' && (node === null || node.data.path !== editingTypeOf)) {
      setEditingTypeOf('')
    }
  })()

  // If we have a remembered path after submitting patches (which refreshes the
  // graph) then we (attempt to) re-select the right node by path.
  useEffect(() => {
    if (rememberedPath !== '') {
      const found = allNodes.find((node:any) => node.data?.path === rememberedPath)
      if (found) {
        setSelectedNodes([found])
      }
      rememberedPath = ''
    }
  }, [allNodes, setSelectedNodes])

  // Any time our selection changes we inform all nodes of the graph, this
  // allows them to populate buttons.
  useEffect(() => {
    const node = getSelectedNode(selectedNodes)
    allNodes.forEach((n:any) => {
      const tmp = n as GraphNode
      if (tmp.data.setSelectedNode) {
        tmp.data.setSelectedNode(node)
      }
    })
  }, [allEdges, allNodes, selectedNodes])

  // When our selection changes we attempt to obtain information about the
  // selected component.
  useEffect(() => {
    let subscribed = true

    const node = getSelectedNode(selectedNodes)
    if (node === null) {
      setInfo(null)
      setKindOptions(null)
      setLoading(false)
      setFetchError(null)
      return
    }

    if (editingTypeOf === '') {
      const cached = cachedInfo[node.data.path || '']
      if (typeof cached === 'object' && cached !== null) {
        setLoading(false)
        setInfo(cached)
        setFetchError(null)
        return
      }
    } else if (node.data.kind) {
      const cached = cachedOptions[node.data.kind]
      if (typeof cached === 'object') {
        setLoading(false)
        setKindOptions(cached)
        setFetchError(null)
        return
      }
    }

    setInfo(null)
    setKindOptions(null)
    setLoading(true)

    if (editingTypeOf !== '') {
      fetch(`/mqttapi/v1/session/${sessionID}/list/${node.data.kind}`, {
        headers: myHeaders,
        method: 'GET',
      })
        .then(async (response) => {
          if (response.ok) {
            setFetchError(null)
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((json) => {
          if (subscribed && node?.data.kind) {
            const kindOptionsStrings: string[] = json.map((ele: kindOption) => ele.name)
            cachedOptions[node.data.kind] = kindOptionsStrings
            setKindOptions(kindOptionsStrings)
          }
        })
        .catch((error) => (subscribed ? setFetchError(error) : null))
        .finally(() => (subscribed ? setLoading(false) : null))

      return () => (subscribed = false)
    }

    let params = new URLSearchParams()
    params.append('path', node.data.path + '/' + node.data.type)
    params.append('type', node.data?.type!)
    if (node.data.nodeId) {
      params.append('path', node.data.path + '/' + node.data.type)
      params.append('type', node.data?.type!)
      params.append('nodeid', node.data.nodeId)
    }
    const myHeader1 = getHeaders()
    fetch(fetchNodeURL + '?' + params.toString(), { headers: myHeader1 })
      .then(async (response) => {
        if (response.ok) {
          const resPdata = await response.json()
          setFetchError(null)
          return resPdata
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((json) => {
        if (subscribed && node?.data.path) {
          cachedInfo[node.data.path] = json
          if (node.data?.type === 'switch') {
            setCheckSwitch(json.config)
            setDeltedNumberIndex([])
          }
          setInfo(json)
        }
      })
      .catch((error) => (subscribed ? setFetchError(error) : null))
      .finally(() => (subscribed ? setLoading(false) : null))

    return () => {
      subscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNodeURL, fileName, sessionID, editingTypeOf, selectedNodes, setKindOptions])

  /**
   * Function for copy node data form nodeEditor
   */
  const copyToClipBoard = async () => {
    try {
      let forPath = info?.path
      if (forPath) {
        let nodePathArray = forPath.split('/')
        let nodeType = nodePathArray[nodePathArray.length - 1]
        let pipelineType = nodePathArray[1]
        let copiedData = {
          typeNode: nodeType,
          pipelineType: pipelineType,
          dataNode: info?.config,
        }
        const textData = JSON.stringify(copiedData)
        await navigator.clipboard.writeText(textData)
      }
      /* Resolved - text copied to clipboard successfully */
    } catch (err) {
      setCopyError(`Failed to copy: ${err}`)
      /* Rejected - text failed to copy to the clipboard */
    }
  }
  /**
   * Function for paste to node editor
   */
  const onPasteNode = async () => {
    const coppyedData = await navigator.clipboard.readText()
    const infoData = JSON.parse(coppyedData)
    let forPath = info?.path
    if (forPath) {
      let nodePathArray = forPath?.split('/')
      let nodeType = nodePathArray[nodePathArray.length - 1]
      let pipelineType = nodePathArray[1]
      if (infoData.typeNode === nodeType) {
        if (infoData.pipelineType === pipelineType) {
          setInfo({ ...info, config: infoData?.dataNode })
        } else {
          setCopyError(`Copied data is only for ${infoData.pipelineType} Pipeline`)
        }
      } else {
        setCopyError(`Copied data is only for ${infoData.typeNode} Type Node`)
      }
    }
  }

  // Close the selection and also wipe any cached changed.
  const discardChanges = useCallback(() => {
    const node = getSelectedNode(selectedNodes)
    if (node !== null) {
      cachedInfo[node.data.path || ''] = null
    }
    if (rememberedPath !== '') {
      rememberedPath = ''
    }
    setInfo(null)
    setSelectedNodes([])
  }, [setInfo, selectedNodes, setSelectedNodes])

  const submitPatches = useCallback(
    (patches: NodeAction[], andThen: () => void, andFinally: () => void) => {
      fetch(fetchNodeURL, {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(patches),
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((res: GraphResponse) => {
          andThen()
          setTree(GraphResToTree(res))
          setSelectedNodes([])
        })
        .catch((error) => setPatchError(error))
        .finally(andFinally)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchNodeURL, setPatchError, setSelectedNodes, setTree]
  )

  const submitDelete = useCallback(() => {
    const deletes: NodeAction[] = []
    ;(selectedNodes || [])
      .filter((node:any) => node.data && (node.data.path || '').length > 0)
      .forEach((node:any) =>
        deletes.push({
          operation: 'delete',
          path: node.data?.path,
          nodeid: node.data?.nodeId,
          label: node.data?.label,
          type: node.data?.type,
        })
      )

    if (deletes.length === 0) {
      return
    }

    deletes.sort((a, b) => {
      if (!a.path) {
        return 1
      }
      if (!b.path) {
        return -1
      }
      if (a.path < b.path) {
        return 1
      }
      if (a.path > b.path) {
        return -1
      }
      return 0
    })

    submitPatches(
      deletes,
      () => {
        deletes.forEach((del) => {
          if (del.path) {
            cachedInfo[del.path] = null
          }
        })
      },
      () => {}
    )
  }, [selectedNodes, submitPatches])

  // Switch our editor view to show the type and label fields for a node.
  const setEditType = useCallback(() => {
    const node = getSelectedNode(selectedNodes)
    if (node === null) {
      return
    }
    setEditingTypeOf(node.data.path || '')
  }, [selectedNodes, setEditingTypeOf])

  // Switch our editor back to the normal view.
  const clearEditType = useCallback(() => {
    setEditingTypeOf('')
  }, [setEditingTypeOf])

  // Submit a type and label change.
  /**
   * Submit a type and label change.
   */
  const submitLabelChange = useCallback(
    (ctx:any) => {
      ctx.preventDefault()
      const node = getSelectedNode(selectedNodes)
      if (node === null) {
        return
      }

      const tmpLabel = (ctx.target.elements.label || {}).value || ''
      const tmpType = ctx.target.elements.typeDropdown.value

      const patches = []
      if (tmpLabel === '') {
        if (node.data.label !== undefined) {
          patches.push({
            operation: 'delete',
            path: node.data.path + '/label',
            nodeid: node.data?.nodeId,
            label: node.data?.label,
            type: node.data?.type,
          })
        }
      } else if (tmpLabel !== node.data.label) {
        patches.push({
          operation: 'set',
          path: node.data.path + '/label',
          value: tmpLabel,
          nodeid: node.data?.nodeId,
          type: node.data?.type,
          label: node.data?.label,
        })
      }
      if (tmpType !== node.data.type) {
        patches.push({
          operation: 'delete',
          path: node.data.path + '/' + node.data.type,
          nodeid: node.data?.nodeId,
          label: node.data?.label,
          type: node.data?.type,
        })
        patches.push({
          operation: 'set',
          path: node.data.path + '/' + tmpType,
          value: null,
          nodeid: node.data?.nodeId,
          type: node.data?.type,
          label: node.data?.label,
        })
      }
      if (patches.length === 0) {
        setEditingTypeOf('')
        return
      }

      submitPatches(
        patches,
        () => {
          if (node?.data.path) {
            cachedInfo[node.data.path] = null
          }
        },
        () => {
          setEditingTypeOf('')
          rememberedPath = node?.data.path || ''
        }
      )
    },
    [submitPatches, setEditingTypeOf, selectedNodes]
  )

  const submitConfigChanges = useCallback(
    (ctx:any) => {
      const node = getSelectedNode(selectedNodes)
      if (node === null) {
        return
      }
      submitPatches(
        [
          {
            operation: 'set',
            path: node.data.path + '/' + node.data.type,
            value:
              node.data?.type === 'switch'
                ? { switch: ctx.formData }
                : node.data?.type === 'resource'
                ? { resource: ctx.formData }
                : node.data?.type === 'custom_switch'
                ? { custom_switch: ctx.formData }
                : ctx.formData,
            nodeid: node.data.nodeId,
            type: node.data?.type,
            deletedData: deltedNumberIndex,
          },
        ],
        () => {
          discardChanges()
        },
        () => {}
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submitPatches, selectedNodes, discardChanges, checkSwitch, findDeletedObjectIndices]
  )

  /**
   * KEYBOARD SHORT CUTS.
   */
  const keyEventHandler = useCallback(
    (event:any) => {
      if (event.target !== document.body) {
        return
      }

      if (event.key === 'Delete') {
        submitDelete()
        return
      }

      // All other handlers require ctrl key
      if (!event.metaKey && !event.ctrlKey) {
        return
      }

      if (event.key === 'Z' || (event.key === 'z' && event.shiftKey)) {
        submitRedo()
        return
      }
      if (event.key === 'z') {
        submitUndo()
        return
      }

      const node = getSelectedNode(selectedNodes)
      if (node === null) {
        return
      }

      if (event.key === 'v') {
        if (copiedNode === null) {
          return
        }

        try {
          const patches = [createCopyPatch(copiedNode, node.data)]
          if (copiedNode.operation === 'cut') {
            patches.push({
              operation: 'delete',
              path: copiedNode.path,
              nodeid: node.data.nodeId,
              label: node.data?.label,
              type: node.data?.type,
            })
          }
          submitPatches(
            patches,
            () => {
              if (copiedNode.operation === 'cut') {
                setCopiedNode(null)
              }
            },
            () => {}
          )
        } catch (err: any) {
          setPatchError(err as Error)
        }
        return
      }

      if (event.key === 'c' || event.key === 'x') {
        setCopiedNode({
          operation: event.key === 'c' ? 'copy' : 'cut',
          kind: node.data.kind,
          path: node.data.path,
          type: node.data?.type,
        })
      }
    },
    [selectedNodes, submitPatches, submitUndo, submitRedo, copiedNode, setCopiedNode, submitDelete]
  )

  /**
   * Any time the editor key handler changes we need to resubscribe.
   */
  useEffect(() => {
    document.addEventListener('keydown', keyEventHandler)
    return () => {
      document.removeEventListener('keydown', keyEventHandler)
    }
  }, [keyEventHandler])

  const node = getSelectedNode(selectedNodes)
  if ((info === null && editingTypeOf === '' && !isLoading && fetchError === null && patchError === null) || node === null) {
    return (
      <div className={styles.controlPanel}>
        <ControlPanel canUndo={canUndo} canRedo={canRedo} onUndo={submitUndo} onRedo={submitRedo}></ControlPanel>
      </div>
    )
  }

  const headerStyle: React.CSSProperties = {
    borderBottom: `solid 2px ${KindColor({ kind: node.data.kind || '' })}`,
  }

  /**
   * Caches any changes made in the editor view.
   */
  const tmpSaveChanges = (ctx: any) => {
    const tmpInfo = info
    if (tmpInfo !== null && typeof tmpInfo === 'object') {
      tmpInfo.config = ctx.formData
      cachedInfo[tmpInfo.path] = tmpInfo
      if (node?.data?.type === 'switch') {
        const newChange = ctx.formData
        if (newChange.length < checkSwitch.length) {
          const deltedNumberIndexdata = findDeletedObjectIndices(checkSwitch, ctx.formData)
          setDeltedNumberIndex(deltedNumberIndexdata)
        }
        setCheckSwitch(ctx.formData)
      }
    }
  }

  if (isLoading) {
    return <Loading />
  }
  if (fetchError !== null) {
    return <ErrorDisplay err={fetchError} url={fetchNodeURL} />
  }

  if (editingTypeOf !== '') {
    return (
      <>
        <ErrorModal title="Failed to apply change" what={patchError} isOpen={patchError !== null} closeModal={() => setPatchError(null)} />
        <h4 style={headerStyle} className={styles.headerStyle}>
          {node.data.type}{' '}
          {node.data.type !== 'resource' ? <span className={styles.titleLabel}>{node.data.label || '<no label>'}</span> : null}
          <button title="back" className={styles.editTypeBtn} onClick={clearEditType}>
            <ArrowBackIcon />
          </button>
        </h4>
        <form onSubmit={submitLabelChange}>
          <div className="form-group">
            <fieldset>
              {node.data.kind !== 'buffer' && (
                <div className="form-group">
                  <label>Label</label>
                  <input className="form-control" type="text" defaultValue={node.data.label || ''} name="label" />
                </div>
              )}
              <div className="form-group">
                <label>Type</label>
                <select className="form-control" name="typeDropdown">
                  {(kindOptions || []).map(
                    (type, idx) => (
                      <option key={idx} value={type} selected={node?.data.type === type}>
                        {type}
                      </option>
                    )
                    // NOTE: We should be using defaultValue={type} but that
                    // only seems to work on the first rendering.
                  )}
                </select>
              </div>
            </fieldset>
          </div>
          <div className="bottomBtns">
            <button type="submit" className="btn btn-info">
              Update
            </button>
            <button className="backBtn btn" onClick={clearEditType}>
              Back
            </button>
          </div>
        </form>
      </>
    )
  }
  /**
   * Function for upload file on switch
   */
  const handleFileUpload = (event: any) => {
    const file = event.target.files[0]
    const reader = new FileReader()

    reader.onload = () => {
      const result = reader.result
      if (typeof result === 'string') {
        const lines = result.split('\n')
        const data = lines.map((line) => line.split(','))
        const csvData: any = convertCsvToConfig(data)
        if (csvData) {
          let configData = info?.config
          let completeConfig = { ...configData, tags: csvData }
          setInfo({ ...info, config: completeConfig })
        }
      } else {
        console.error('File contents not in expected string format')
      }
    }

    reader.readAsText(file)
  }
  // Function for add custom switch data by csv file
  /**
   * @param {string[][]}  data - Csv Data.
   * @returns {array} - return data of custom switch schema
   */
  const convertCsvToConfig = (data: string[][]) => {
    const result = []

    for (let i = 1; i < data.length - 1; i++) {
      const [device_id, tag_name, tag_value, measurement_name] = data[i]

      result.push({
        check: `this.device == "${device_id}"`,
        tag_name: tag_name,
        tag_value,
        measurement_name,
      })
    }
    return result
  }
  // Function for check and delete custom switch data
  /**
   * @param {any}  previousData - previous custom switch data.
   * @param {any}  newData - new custom switch data.
   * @returns {any[]} - return data of custom switch schema
   */
  function findDeletedObjectIndices(previousData: any, newData: any) {
    const deletedIndexes = []

    for (let i = 0; i < previousData.length; i++) {
      const prevObj = previousData[i]
      let found = false

      for (let j = 0; j < newData.length; j++) {
        const newObj = newData[j]

        if (prevObj.name === newObj.name) {
          found = true
          break
        }
      }

      if (!found) {
        deletedIndexes.push(i)
      }
    }

    return deletedIndexes
  }

  const lintErrors = node.data.lintErrors || []
  return (
    <>
      <ErrorModal title="Failed to apply change" what={patchError} isOpen={patchError !== null} closeModal={() => setPatchError(null)} />
      <ButtonGroup size="small" variant="outlined" color="secondary" aria-label="outlined primary button group">
        <Button endIcon={<ContentCopyIcon />} onClick={copyToClipBoard}>
          Copy Node
        </Button>
        <Button endIcon={<ContentPasteGoIcon />} onClick={onPasteNode}>
          Paste Node
        </Button>
      </ButtonGroup>
      {copyError && (
        <div className={styles.lintErrorSection}>
          <h3>Copy Error</h3>
          <pre>{copyError}</pre>
        </div>
      )}
      <h4 style={headerStyle} className={styles.headerStyle}>
        <div className={styles.titleNames}>
          <div>{node.data.type}</div>
          <div>{node.data.type !== 'resource' ? <span className={styles.titleLabel}>{node.data.label || '<no label>'}</span> : null}</div>
        </div>
        {readOnly ? (
          <div className={styles.deleteBtn}></div>
        ) : (
          <>
            <button title="edit type and/or label" className={styles.editTypeBtn} onClick={setEditType}>
              <EditIcon />
            </button>
            <button title="delete component" className={styles.deleteBtn} onClick={submitDelete}>
              <DeleteIcon />
            </button>
          </>
        )}
      </h4>
      {lintErrors.length > 0 && (
        <div className={styles.lintErrorSection}>
          <h3>Lint Errors</h3>
          {lintErrors.map((errString, idx) => (
            <pre key={idx}>{errString}</pre>
          ))}
          <small>
            Hint: Some lint errors are easier to fix from the <Link to={configURL}>config view</Link>.
          </small>
        </div>
      )}
      {node.data.type === 'custom_switch' ? (
        <div>
          <InputLabel>Upload CSV File</InputLabel>
          <Input type="file" placeholder="upload csv file" onChange={handleFileUpload} />
        </div>
      ) : null}
      {info?.schema ? (
        <Form
          schema={info.schema}
          uiSchema={info.uiSchema}
          formData={info.config}
          onChange={tmpSaveChanges}
          onSubmit={submitConfigChanges}
          onCancel={discardChanges}
          disabled={readOnly}
        />
      ) : (
        <div style={{ textAlign: 'center', maxWidth: '350px' }}>
          <p>Sorry, this component cannot be configured with this view (yet)</p>
          <img width="200px" src="/img/missing.svg" alt="missing" />
        </div>
      )}
    </>
  )
}
